import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Plus, Search, FolderOpen, Users, Calendar,
  MoreVertical, Pencil, Trash2, Briefcase, Package, UserCircle, Clock,
  Building, Folder, Lightbulb, Tag, ClipboardList, Power, PowerOff,
  SlidersHorizontal, X,
} from 'lucide-react';

import { PageHeader } from '@/components/layout/PageHeader';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { EmptyState } from '@/components/shared/EmptyState';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { GroupMembersDialog } from '@/components/groups/GroupMembersDialog';
import { CursorPagination } from '@/components/shared/CursorPagination';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { groupService, GROUP_CATEGORIES, type Group, type GroupListParams } from '@/services/group.service';

// ---------------------------------------------------------------------------
// Category config (matching old system)
// ---------------------------------------------------------------------------
const categoryConfig: Record<string, { icon: React.ElementType; label: string; className: string }> = {
  project: { icon: Briefcase, label: 'Project', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  product: { icon: Package, label: 'Product', className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' },
  team: { icon: UserCircle, label: 'Team', className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  period: { icon: Clock, label: 'Period', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' },
  company: { icon: Building, label: 'Company', className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200' },
  department: { icon: Folder, label: 'Department', className: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200' },
  initiative: { icon: Lightbulb, label: 'Initiative', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
  other: { icon: Tag, label: 'Other', className: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200' },
  release: { icon: Tag, label: 'Release', className: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200' },
  feature: { icon: Tag, label: 'Feature', className: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200' },
  cr: { icon: Tag, label: 'CR', className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
  sla: { icon: ClipboardList, label: 'SLA', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' },
};

function CategoryBadgeIcon({ category }: { category: string }) {
  const config = categoryConfig[category];
  if (!config) return null;
  const Icon = config.icon;
  return (
    <Badge variant="outline" className={config.className}>
      <Icon className="h-3 w-3 mr-1" />
      {config.label}
    </Badge>
  );
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days < 1) return 'Today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
}

// ---------------------------------------------------------------------------
// Create Group schema
// ---------------------------------------------------------------------------
const createSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().max(2000).optional(),
  category: z.string().optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  enable_project_team: z.boolean().optional(),
  owner_id: z.string().optional(),
}).refine(
  (d) => !d.start_date || !d.end_date || new Date(d.end_date) >= new Date(d.start_date),
  { message: 'End date must be on or after start date', path: ['end_date'] }
);
type CreateFormData = z.infer<typeof createSchema>;

// ---------------------------------------------------------------------------
// Groups Page
// ---------------------------------------------------------------------------
export function GroupsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, isAdmin } = useAuth();

  const [search, setSearch] = useState('');
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [page, setPage] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');

  // Delete state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Group | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Edit state
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Group | null>(null);
  const editForm = useForm<CreateFormData>({ resolver: zodResolver(createSchema) });
  const [editLoading, setEditLoading] = useState(false);

  // Members dialog state
  const [membersOpen, setMembersOpen] = useState(false);
  const [membersTarget, setMembersTarget] = useState<Group | null>(null);

  const hasFilters = !!categoryFilter || !!ownerFilter;
  const activeFilterCount = [categoryFilter, ownerFilter].filter(Boolean).length;

  function clearFilters() {
    setCategoryFilter('');
    setOwnerFilter('');
    setPage(0);
    setCursors([undefined]);
  }

  const params: GroupListParams = {
    cursor: cursors[page],
    limit: 30,
    sort: 'created_at',
    order: 'desc',
    ...(search && { search }),
    ...(categoryFilter && { category: categoryFilter }),
    ...(ownerFilter && { owner_id: ownerFilter }),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['groups', params],
    queryFn: () => groupService.list(params).then((r) => r.data),
    placeholderData: (prev) => prev,
  });

  const groups = data?.data ?? [];
  const pagination = data?.pagination;

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const createForm = useForm<CreateFormData>({ resolver: zodResolver(createSchema) });
  const [createLoading, setCreateLoading] = useState(false);

  const createMutation = useMutation({
    mutationFn: (formData: CreateFormData) =>
      groupService.create({
        name: formData.name,
        description: formData.description || undefined,
        category: formData.category || undefined,
        start_date: formData.start_date || undefined,
        end_date: formData.end_date || undefined,
        enable_project_team: formData.enable_project_team || false,
        owner_id: formData.owner_id || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast({ title: 'Group created successfully' });
      setCreateOpen(false);
      createForm.reset();
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.response?.data?.error?.message || 'Failed to create group', variant: 'destructive' });
    },
    onSettled: () => setCreateLoading(false),
  });

  const editMutation = useMutation({
    mutationFn: (formData: CreateFormData) =>
      groupService.update(editTarget!.id, {
        name: formData.name,
        description: formData.description || null,
        category: formData.category || undefined,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast({ title: 'Group updated successfully' });
      setEditOpen(false);
      setEditTarget(null);
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.response?.data?.error?.message || 'Update failed', variant: 'destructive' });
    },
    onSettled: () => setEditLoading(false),
  });

  const deleteMutation = useMutation({
    mutationFn: () => groupService.delete(deleteTarget!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast({ title: 'Group deleted successfully' });
      setDeleteOpen(false);
      setDeleteTarget(null);
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.response?.data?.error?.message || 'Delete failed', variant: 'destructive' });
    },
    onSettled: () => setDeleteLoading(false),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      groupService.update(id, { is_active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast({ title: 'Group status updated' });
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.response?.data?.error?.message || 'Failed', variant: 'destructive' });
    },
  });

  const canEdit = (g: Group) => isAdmin || g.owner_id === user?.id;

  function openEdit(group: Group) {
    setEditTarget(group);
    editForm.reset({
      name: group.name, description: group.description ?? '',
      category: group.category, start_date: group.start_date ?? '', end_date: group.end_date ?? '',
    });
    setEditOpen(true);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Groups"
        description="Manage your groups and team collaboration"
      />

      <div className="space-y-6">
        {/* Toolbar */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search groups..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); setCursors([undefined]); }}
              className="pl-9"
            />
          </div>
          <Button
            variant={showFilters ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="gap-1.5"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </Button>
          <Button size="sm" className="bg-brand-primary hover:bg-brand-primary-dark ml-auto" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1 h-4 w-4" /> Create Group
          </Button>
        </div>

        {/* Collapsible filter row */}
        {showFilters && (
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); setCursors([undefined]); }}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm min-w-[160px] focus:outline-none focus:ring-1 focus:ring-brand-primary dark:bg-background dark:border-border"
            >
              <option value="">All Categories</option>
              {GROUP_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
              ))}
            </select>
            <select
              value={ownerFilter}
              onChange={(e) => { setOwnerFilter(e.target.value); setPage(0); setCursors([undefined]); }}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm min-w-[160px] focus:outline-none focus:ring-1 focus:ring-brand-primary dark:bg-background dark:border-border"
            >
              <option value="">All Owners</option>
              {Array.from(new Map(groups.filter((g) => g.owner).map((g) => [g.owner_id, g.owner!.full_name])).entries()).map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* Card grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-52 rounded-lg" />)}
          </div>
        ) : groups.length === 0 ? (
          <EmptyState
            icon={FolderOpen}
            title={search ? 'No groups found' : 'No groups yet'}
            description={search ? 'Try adjusting your search' : 'Create your first group to start collaborating'}
            actionLabel={!search ? 'Create Group' : undefined}
            onAction={!search ? () => setCreateOpen(true) : undefined}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groups.map((group) => (
              <Card
                key={group.id}
                className={`cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-lg h-full flex flex-col ${!group.is_active ? 'opacity-60' : ''}`}
                onClick={() => navigate(`/groups/${group.id}`)}
              >
                <div className="p-4 flex-1 flex flex-col">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="font-mono text-xs bg-muted">
                          {group.reference_id}
                        </Badge>
                        <span className="text-sm font-semibold truncate">{group.name}</span>
                        {!group.is_active && <Badge variant="outline" className="bg-muted text-xs">Inactive</Badge>}
                        <CategoryBadgeIcon category={group.category} />
                      </div>
                      {group.default_team_id && (
                        <div className="mt-1">
                          <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-xs">
                            <ClipboardList className="h-3 w-3 mr-1" /> Project Team
                          </Badge>
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        Owner: {group.owner?.full_name || 'Unknown'}
                      </p>
                    </div>
                    {canEdit(group) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEdit(group); }}>
                            <Pencil className="h-4 w-4 mr-2" /> Edit Group
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            toggleMutation.mutate({ id: group.id, is_active: !group.is_active });
                          }}>
                            {group.is_active ? (
                              <><PowerOff className="h-4 w-4 mr-2" /> Deactivate Group</>
                            ) : (
                              <><Power className="h-4 w-4 mr-2" /> Activate Group</>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(group); setDeleteOpen(true); }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> Delete Group
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-xs text-muted-foreground mt-3 line-clamp-2 flex-1">
                    {group.description || 'No description provided'}
                  </p>

                  {/* Footer */}
                  <div className="flex items-center justify-between mt-4 pt-3 border-t">
                    <div className="flex flex-col gap-1.5">
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Users className="h-3.5 w-3.5" /> {group.members_count ?? 0} members
                      </span>
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Calendar className="h-3.5 w-3.5" /> Created {timeAgo(group.created_at)}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={(e) => { e.stopPropagation(); setMembersTarget(group); setMembersOpen(true); }}
                    >
                      Members
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination && (pagination.has_more || page > 0) && (
          <CursorPagination
            hasNext={pagination.has_more}
            hasPrev={page > 0}
            onNext={() => {
              if (pagination.cursor) {
                setCursors((p) => { const n = [...p]; n[page + 1] = pagination.cursor!; return n; });
                setPage((p) => p + 1);
              }
            }}
            onPrev={() => setPage((p) => Math.max(0, p - 1))}
          />
        )}
      </div>

      {/* Create Group Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Create New Group</DialogTitle>
            <DialogDescription>Create a new group to collaborate with team members</DialogDescription>
          </DialogHeader>
          <form onSubmit={createForm.handleSubmit((d) => { setCreateLoading(true); createMutation.mutate(d); })} className="space-y-4">
            <div className="space-y-2">
              <Label>Group Name</Label>
              <Input placeholder="Enter group name" {...createForm.register('name')} />
              {createForm.formState.errors.name && <p className="text-xs text-destructive">{createForm.formState.errors.name.message}</p>}
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Enter group description"
                {...createForm.register('description')}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Category (Optional)</Label>
              <Select value={createForm.watch('category') || ''} onValueChange={(v) => createForm.setValue('category', v)}>
                <SelectTrigger><SelectValue placeholder="Select a category" /></SelectTrigger>
                <SelectContent>
                  {GROUP_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isAdmin && <OwnerSelect value={createForm.watch('owner_id') || ''} onChange={(v) => createForm.setValue('owner_id', v)} />}
            <div className="space-y-2">
              <Label>Start Date (Optional)</Label>
              <Input type="date" {...createForm.register('start_date')} />
            </div>
            <div className="space-y-2">
              <Label>End Date (Optional)</Label>
              <Input type="date" {...createForm.register('end_date')} />
              {createForm.formState.errors.end_date && <p className="text-xs text-destructive">{createForm.formState.errors.end_date.message}</p>}
            </div>
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <p className="text-sm font-medium">Enable Project Team</p>
                <p className="text-sm text-muted-foreground">Create a team for flat tasks without goals or sprints</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  {...createForm.register('enable_project_team')}
                />
                <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:bg-primary peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all" />
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={createLoading}>Cancel</Button>
              <Button type="submit" disabled={createLoading}>{createLoading ? 'Creating...' : 'Create Group'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Edit Group</DialogTitle>
            <DialogDescription>Update group information</DialogDescription>
          </DialogHeader>
          <form onSubmit={editForm.handleSubmit((d) => { setEditLoading(true); editMutation.mutate(d); })} className="space-y-4">
            <div className="space-y-2">
              <Label>Group Name</Label>
              <Input placeholder="Enter group name" {...editForm.register('name')} />
              {editForm.formState.errors.name && <p className="text-xs text-destructive">{editForm.formState.errors.name.message}</p>}
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Enter group description"
                {...editForm.register('description')}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={editForm.watch('category') || ''} onValueChange={(v) => editForm.setValue('category', v)}>
                <SelectTrigger><SelectValue placeholder="Select a category" /></SelectTrigger>
                <SelectContent>
                  {GROUP_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Start Date</Label><Input type="date" {...editForm.register('start_date')} /></div>
              <div className="space-y-2"><Label>End Date</Label><Input type="date" {...editForm.register('end_date')} /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)} disabled={editLoading}>Cancel</Button>
              <Button type="submit" disabled={editLoading}>{editLoading ? 'Updating...' : 'Update Group'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Are you sure?"
        description={`This will permanently delete the group "${deleteTarget?.name}" and all related goals, sprints, and tasks. This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        isLoading={deleteLoading}
        onConfirm={() => { setDeleteLoading(true); deleteMutation.mutate(); }}
      />

      {/* Members Dialog */}
      {membersTarget && (
        <GroupMembersDialog
          key={membersTarget.id}
          open={membersOpen}
          onOpenChange={(v) => { setMembersOpen(v); if (!v) setMembersTarget(null); }}
          groupId={membersTarget.id}
          groupName={membersTarget.name}
          canManage={canEdit(membersTarget)}
          canChangeRoles={canEdit(membersTarget)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Owner Select (workspace members dropdown, admin only)
// ---------------------------------------------------------------------------
function OwnerSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [searchVal, setSearchVal] = useState('');
  const [suggestions, setSuggestions] = useState<import('@/services/group.service').WorkspaceUser[]>([]);
  const [open, setOpen] = useState(false);
  const wsId = localStorage.getItem('active_workspace_id') || '';

  async function handleSearch(q: string) {
    setSearchVal(q);
    if (q.length >= 2 && wsId) {
      try {
        const res = await groupService.searchWorkspaceMembers(wsId, q);
        setSuggestions((res.data.data || []).map((m) => m.user));
        setOpen(true);
      } catch { setSuggestions([]); }
    } else {
      setSuggestions([]);
      setOpen(false);
    }
  }

  const selectedName = value ? suggestions.find((u) => u.id === value)?.full_name : undefined;

  return (
    <div className="space-y-2">
      <Label>Group Owner</Label>
      <div className="relative">
        <Input
          placeholder={selectedName || 'Search workspace members...'}
          value={searchVal}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
        />
        {open && suggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border bg-popover shadow-md max-h-48 overflow-y-auto">
            {suggestions.map((u) => (
              <button
                key={u.id}
                type="button"
                className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-accent transition-colors"
                onMouseDown={(e) => { e.preventDefault(); onChange(u.id); setSearchVal(u.full_name); setOpen(false); }}
              >
                <UserAvatar name={u.full_name} avatarUrl={u.avatar_url} size="xs" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{u.full_name}</p>
                  <p className="text-xs text-muted-foreground">{u.email}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
